import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Toolbar,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  TablePagination,
  InputBase,
  InputAdornment,
} from '@mui/material';
import { TableRowSkeleton, DocumentCardSkeleton } from '../components/LoadingSkeleton';
import QueryTable from '../components/QueryTable';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  UploadFile as UploadFileIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  FilePresent as FilePresentIcon, // Added FilePresentIcon
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Comment as CommentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  LinearProgress, // Added LinearProgress
} from '@mui/material';
import { formatDate } from '../utils/mockData';
import { formatBytes } from '../utils/formatters';
import { Project, ProjectFile, ProjectFolder, User, ProjectLifecycle, ProjectStage, ProjectUser, ProjectPriority } from '../types';
import { useAuth } from '../contexts/AuthContext';
import DealDetailsCard from '../components/DealDetailsCard'; // Import the new component
import { getDDQQuestions, updateDDQQuestion, getProjectById, getProjectUsers, removeUserFromProject, addUserToProject, uploadFileToProject, getProjectFiles, getProjectStats, deleteFile, deleteFolder, downloadFile, downloadFolder, deleteProject, getOrganizationUsers, updateProject, getProjectStageDescriptions, getIntegrations, getIntegrationFiles, uploadDocumentFromIntegrationToProject, bulkDeleteFiles } from '../services/api';
import { Integration, IntegrationCategory, IntegrationStatus } from '../types/integrations';

interface StageDescription {
  [key: string]: string;
}

interface ProjectStats {
  fileCount: number;
  totalSize: number;
  aiReadyCount: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  sx?: object;
  idPrefix?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, sx, idPrefix = 'project-detail-tab', ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${idPrefix}-panel-${index}`}
      aria-labelledby={`${idPrefix}-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3, ...sx }}>{children}</Box>}
    </div>
  );
}

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [projectStats, setProjectStats] = useState<ProjectStats>({ fileCount: 0, totalSize: 0, aiReadyCount: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [ddqTabValue, setDdqTabValue] = useState(0);
  const [ddqQuestions, setDdqQuestions] = useState<any[]>([]);
  const [ddqLoading, setDdqLoading] = useState<boolean>(false);
  const [ddqError, setDdqError] = useState<string | null>(null);
  const [ddqSearchQuery, setDdqSearchQuery] = useState('');
  const [ddqPage, setDdqPage] = useState(0);
  const [ddqRowsPerPage, setDdqRowsPerPage] = useState(10);
  const [totalDdqQuestions, setTotalDdqQuestions] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<any | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);
  const [teamPage, setTeamPage] = useState(0);
  const [teamRowsPerPage, setTeamRowsPerPage] = useState(10);
  const [totalTeamMembers, setTotalTeamMembers] = useState(0);
  const [searchTerm] = useState('');
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<ProjectUser | null>(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [userToInvite, setUserToInvite] = useState<User | null>(null);
  const [users, setUsers] = useState<ProjectUser[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);
  const [reviewerCandidates, setReviewerCandidates] = useState<any[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(false); // New state for team loading
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState<boolean>(false); // State for organization users loading
  const [reviewerCandidatesLoading, setReviewerCandidatesLoading] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const folderInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null);
  const [documentType, setDocumentType] = useState<string>('other');
  const [uploadType, setUploadType] = useState<'file' | 'folder'>('file');
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<ProjectFile | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<ProjectFolder | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteProjectDialogOpen, setDeleteProjectDialogOpen] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [stageDescriptions, setStageDescriptions] = useState<StageDescription>({});
  const [uploadTabValue, setUploadTabValue] = useState(0);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [noIntegrationsConnected, setNoIntegrationsConnected] = useState(false);
  const [integrationFiles, setIntegrationFiles] = useState<any[]>([]);
  const [integrationFilesLoading, setIntegrationFilesLoading] = useState(false);
  const [isSubfolderLoading, setIsSubfolderLoading] = useState(false);
  const [selectedIntegrationFilesForUpload, setSelectedIntegrationFilesForUpload] = useState<any[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([{ id: 'root', name: 'My Drive' }]);
  const [downloadingFileIds, setDownloadingFileIds] = useState<Set<string>>(new Set());
  const [downloadingFolderIds, setDownloadingFolderIds] = useState<Set<string>>(new Set());
  const [passwordPrompt, setPasswordPrompt] = useState<{ file: File; fileName: string } | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [uploadStatus, setUploadStatus] = useState<Map<string, 'pending' | 'uploading' | 'success' | 'error'>>(new Map());

  const fetchProjectStats = useCallback(async () => {
    if (!projectId) return;
    try {
      const stats = await getProjectStats(projectId);
      setProjectStats(prevStats => ({
        ...prevStats,
        fileCount: stats.total_files,
        totalSize: stats.total_size,
        aiReadyCount: stats.ai_ready_count || 0, // Assuming API returns ai_ready_count
      }));
    } catch (err) {
      console.error('Failed to fetch project stats', err);
    }
  }, [projectId, setProjectStats]);

  const fetchFiles = useCallback(async (currentPage: number, currentRowsPerPage: number) => {
    if (!projectId) return;
    setFilesLoading(true);
    try {
      const fileData = await getProjectFiles(projectId, {
        page: currentPage + 1, // API expects 1-based page number
        size: currentRowsPerPage,
        search: searchTerm,
      });
      const files = fileData.files;
      setTotalFiles(fileData.total);
      setProject(prevProject => {
        if (!prevProject) return null;
        return {
          ...prevProject,
          files: files || [],
        };
      });
    } catch (err) {
      console.error('Failed to fetch files', err);
    } finally {
      setFilesLoading(false);
    }
  }, [projectId, searchTerm, setFilesLoading, setTotalFiles]);

  const fetchFolders = useCallback(async () => {
    if (!projectId) return;
    try {
      // const folderData = await getProjectFolders(projectId);
      // const folders: ProjectFolder[] = folderData.files.filter((f: any) => f.is_folder);
      // const transformedFolders = transformFolders(folders);
      // setProject(prevProject => {
      //   if (!prevProject) return null;
      //   return {
      //     ...prevProject,
      //     folders: transformedFolders,
      //   };
      // });
    } catch (err) {
      console.error('Failed to fetch folders', err);
    }
  }, [projectId]);
  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !project) return;
    setIsCheckingPassword(true);

    try {
      const file = passwordPrompt.file;

      setUploadStatus(prev => new Map(prev).set(file.name, 'uploading'));

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev.get(file.name) || 0;
          if (current < 90) {
            return new Map(prev).set(file.name, current + 10);
          }
          return prev;
        });
      }, 200);

      await uploadFileToProject(project.id, [file], documentType, password);

      clearInterval(progressInterval);

      setUploadProgress(prev => new Map(prev).set(file.name, 100));
      setUploadStatus(prev => new Map(prev).set(file.name, 'success'));

      setPasswordPrompt(null);
      setPassword('');

      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: `${file.name} uploaded successfully` }));
      fetchProjectStats(); // Refresh project stats
      await fetchFiles(page, rowsPerPage);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setSelectedFolderName(null);
      setPasswordPrompt(null);
      setPassword('');
    } catch (error: any) {
      console.error('Password upload error:', error);

      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.startsWith('incorrect_password:')) {
        window.dispatchEvent(new CustomEvent('apiError', { detail: 'Incorrect password. Please try again.' }));
      } else {
        if (passwordPrompt) {
          setUploadStatus(prev => new Map(prev).set(passwordPrompt.fileName, 'error'));
        }
        window.dispatchEvent(new CustomEvent('apiError', { detail: `Failed to upload: ${errorDetail || error.message}` }));
        setPasswordPrompt(null);
        setPassword('');
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordPrompt(null);
    setPassword('');
  };

  // Polling effect to check for processing documents
  useEffect(() => {
    if (!project || !project.files || !projectId) return;

    // Check if there are any documents with processing status
    const hasProcessingDocs = project.files.some(
      (file: any) => 
        file.processing_status === 'processing' || 
        file.embedding_status === 'processing'
    );

    if (hasProcessingDocs) {
      console.log('Starting polling - found processing documents');
      // Start polling every 3 seconds
      const interval = setInterval(async () => {
        console.log('Polling for document status updates...');
        try {
          // Pass current pagination and search parameters to getProjectFiles
          const fileData = await getProjectFiles(projectId, {
            page: page + 1,
            size: rowsPerPage,
            search: searchTerm,
          });
          const files = fileData.files;
          setTotalFiles(fileData.total); // Update total files from polling response
          setProject(prevProject => {
            if (!prevProject) return null;
            return {
              ...prevProject,
              files: files || [],
            };
          });
          fetchProjectStats(); // Refresh project stats during polling
        } catch (err) {
          console.error('Failed to fetch files during polling', err);
        }
      }, 3000);

      // Cleanup on unmount or when polling should stop
      return () => {
        console.log('Stopping polling');
        clearInterval(interval);
      };
    }
  }, [project, projectId, page, rowsPerPage, searchTerm, fetchProjectStats]);


  useEffect(() => {
    const fetchStageDescriptions = async () => {
      try {
        const data = await getProjectStageDescriptions();
        setStageDescriptions(data);
      } catch (err) {
        console.error('Error fetching stage descriptions:', err);
      }
    };

    fetchStageDescriptions();
  }, []);

  // useEffect to fetch project details (only depends on projectId)
  useEffect(() => {
    let isMounted = true;

    const fetchProjectDetails = async () => {
      if (!projectId || !isMounted) return;

      try {
        setLoading(true);
        const projectData = await getProjectById(projectId);

        if (!isMounted) return;

        if (projectData) {
          const transformedData = {
            ...projectData,
            organizationId: projectData.organization_id,
            ownerId: projectData.owner_id,
            dealId: projectData.deal_id,
            createdDate: projectData.created_at,
            updatedDate: projectData.updated_at,
            tags: projectData.tags || [],
            users: projectData.users || [],
            queryCount: projectData.query_count || 0,
            files: [],
            folders: [],
            queries: projectData.queries || [],
            lifecycle: projectData.lifecycle || ProjectLifecycle.InProcess,
            stage: projectData.stage || ProjectStage.Initiation,
            isPrivate: projectData.is_private,
            priority: projectData.priority,
          };
          setProject(transformedData);
          fetchProjectStats(); // Fetch project stats after setting project
          // Explicitly fetch files after project details are loaded
          await fetchFiles(page, rowsPerPage);
          setError(null);
        } else {
          setError('Project not found.');
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to fetch project details.');
          console.error(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProjectDetails();

    return () => {
      isMounted = false;
    };
  }, [projectId, fetchProjectStats, fetchFiles, page, rowsPerPage, setProject, setLoading, setError]);

  // useEffect to fetch files and folders (depends on projectId, page, rowsPerPage, searchTerm, tabValue)
  useEffect(() => {
    let isMounted = true;

    const fetchFilesAndFolders = async () => {
      if (!projectId || !isMounted) return; // projectId is from useParams, so it's stable after initial load

      if (tabValue === 0) { // Only fetch files if on the Files tab
        await fetchFiles(page, rowsPerPage);
        await fetchFolders();
      }
    };

    fetchFilesAndFolders();

    return () => {
      isMounted = false;
    };
  }, [projectId, page, rowsPerPage, searchTerm, tabValue, fetchFiles, fetchFolders]);

  // Fetch project team members only when the "Team" tab is active
  useEffect(() => {
    let isMounted = true;

    const fetchProjectTeamData = async () => {
      if (!projectId || tabValue !== 3 || !isMounted) return; // Only fetch if Team tab is active

      setTeamLoading(true);
      try {
        const projectUsersResponse = await getProjectUsers(projectId, { page: teamPage + 1, size: teamRowsPerPage });
        if (!isMounted) return;

        setUsers(projectUsersResponse.members);
        setTotalTeamMembers(projectUsersResponse.total);
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch project team data', err);
        }
      } finally {
        if (isMounted) {
          setTeamLoading(false);
        }
      }
    };

    fetchProjectTeamData();

    return () => {
      isMounted = false;
    };
  }, [projectId, tabValue, teamPage, teamRowsPerPage, setUsers, setTotalTeamMembers, setTeamLoading]);

  // Fetch organization users only when invite dialog opens and data is not present
  useEffect(() => {
    let isMounted = true;

    const fetchOrganizationUsersData = async () => {
      if (!projectId || !inviteDialogOpen || !isMounted || organizationUsers.length > 0) return; // Only fetch if dialog is open and users not already fetched

      setOrganizationUsersLoading(true);
      try {
        const orgUsersResponse = await getOrganizationUsers(project?.organizationId!);
        if (!isMounted) return;
        const activeOrgUsers = orgUsersResponse.members.filter((orgUser: any) => orgUser.user.is_active);
        setOrganizationUsers(activeOrgUsers);
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch organization users', err);
        }
      } finally {
        if (isMounted) {
          setOrganizationUsersLoading(false);
        }
      }
    };

    fetchOrganizationUsersData();

    return () => {
      isMounted = false;
    };
  }, [projectId, inviteDialogOpen, project?.organizationId, organizationUsers.length]);

  // Fetch organization users only when invite dialog opens and data is not present
  useEffect(() => {
    let isMounted = true;

    const fetchOrganizationUsersData = async () => {
      if (!projectId || !inviteDialogOpen || !isMounted || organizationUsers.length > 0) return; // Only fetch if dialog is open and users not already fetched

      setOrganizationUsersLoading(true);
      try {
        const orgUsersResponse = await getOrganizationUsers(project?.organizationId!);
        if (!isMounted) return;
        const activeOrgUsers = orgUsersResponse.members.filter((orgUser: any) => orgUser.user.is_active);
        setOrganizationUsers(activeOrgUsers);
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch organization users', err);
        }
      } finally {
        if (isMounted) {
          setOrganizationUsersLoading(false);
        }
      }
    };

    fetchOrganizationUsersData();

    return () => {
      isMounted = false;
    };
  }, [projectId, inviteDialogOpen, project?.organizationId, organizationUsers.length]);

  // Fetch DDQ questions when the DDQ tab is active
  useEffect(() => {
    const fetchDDQQuestions = async () => {
      if (!projectId || tabValue !== 2) return; // Only fetch if DDQ tab is active

      setDdqLoading(true);
      setDdqError(null);
      try {
        let status = 'all';
        if (ddqTabValue === 1) {
          status = 'needs_review';
        } else if (ddqTabValue === 2) {
          status = 'completed';
        } else if (ddqTabValue === 3) {
          status = 'unassigned';
        }
        const data = await getDDQQuestions(projectId, status, { page: ddqPage + 1, size: ddqRowsPerPage });
        setDdqQuestions(data.questions || []);
        setTotalDdqQuestions(data.total || 0);
      } catch (err) {
        console.error('Failed to fetch DDQ questions', err);
        setDdqError('Failed to fetch DDQ questions.');
      } finally {
        setDdqLoading(false);
      }
    };

    fetchDDQQuestions();
  }, [projectId, tabValue, ddqTabValue, ddqPage, ddqRowsPerPage]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIntegrationsLoading(true);
        const integrationsResponse = await getIntegrations();

        if (integrationsResponse && integrationsResponse.length === 0) {
          setNoIntegrationsConnected(true);
          setIntegrations([]);
        } else {
          setNoIntegrationsConnected(false);
          const integrations = integrationsResponse.map((apiIntegration: { id: string; tool_name: string }) => {
            const displayName = apiIntegration.tool_name.charAt(0).toUpperCase() + apiIntegration.tool_name.slice(1);
            
            return {
              id: apiIntegration.id,
              name: displayName,
              description: `${displayName} integration`,
              category: IntegrationCategory.DocumentRepository,
              status: IntegrationStatus.Connected,
              isConnected: true,
            };
          });
          setIntegrations(integrations);
        }
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        setNoIntegrationsConnected(true); // Assume no integrations if API call fails
        setIntegrations([]);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    if (uploadDialogOpen) {
      fetchInitialData();
    }
  }, [uploadDialogOpen]);

  useEffect(() => {
    if (selectedIntegration) {
      fetchIntegrationFiles(selectedIntegration, currentFolderId);
    }
  }, [selectedIntegration, currentFolderId]);

  const fetchIntegrationFiles = async (integration: Integration, folderId: string = 'root') => {
    if (!integration.isConnected) {
      return;
    }
    if (folderId === 'root') {
      setIntegrationFilesLoading(true);
    } else {
      setIsSubfolderLoading(true);
    }
    setIntegrationFiles([]);
    try {
      const integrationName = integration.name.toLowerCase().replace(/\s/g, '');
      const response = await getIntegrationFiles(integrationName, folderId);
      
      if (response.status === 200) {
          const data = response.data;
          let files: any[] = [];
          if (integrationName === 'googledrive') {
            files = (data.files || []).map((file: any) => ({
                id: file.id,
                name: file.name,
                modified_time: file.modifiedTime,
                size: file.size ? parseInt(file.size, 10) : 0,
                mimeType: file.mimeType,
                isFolder: file.mimeType === 'application/vnd.google-apps.folder',
              }));
          } else if (integrationName === 'dropbox') {
            files = (data.entries || []).map((entry: any) => ({
                id: entry.path_display,
                name: entry.name,
                modified_time: entry.server_modified,
                size: entry.size,
                isFolder: entry['.tag'] === 'folder',
              }));
          } else if (integrationName === 'box') {
            files = (data.entries || []).map((entry: any) => ({
                id: entry.id,
                name: entry.name,
                modified_time: entry.modified_at,
                size: entry.size,
                isFolder: entry.type === 'folder',
              }));
          } else if (integrationName === 'onedrive') {
             files = (data.value || []).map((entry: any) => ({
               id: entry.id,
               name: entry.name,
               modified_time: entry.lastModifiedDateTime,
               size: entry.size,
               isFolder: !!entry.folder,
             }));
           }
          setIntegrationFiles(files);
      } else {
          console.error(`Failed to fetch files for ${integration.name}`);
          setIntegrationFiles([]);
      }
    } catch (error) {
      console.error(`Error fetching files for ${integration.name}:`, error);
      setIntegrationFiles([]);
    } finally {
      setIntegrationFilesLoading(false);
      setIsSubfolderLoading(false);
    }
  };

  const handleIntegrationFileSelect = (file: any, isSelected: boolean) => {
    if (isSelected) {
      const newFile = {
        id: file.id,
        name: file.name,
        size: file.size || 0,
        type: file.mimeType || 'application/octet-stream',
        source: selectedIntegration?.name,
      };
      setSelectedIntegrationFilesForUpload(prev => [...prev, newFile]);
    } else {
      setSelectedIntegrationFilesForUpload(prev => prev.filter(f => f.id !== file.id));
    }
  };

  const handleIntegrationUpload = async () => {
    if (selectedIntegrationFilesForUpload.length === 0 || !selectedIntegration || !projectId) return;

    setIsUploading(true);
    
    const newStatus = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
    selectedIntegrationFilesForUpload.forEach(file => {
      newStatus.set(file.name, 'uploading'); // Set initial status to uploading
    });
    setUploadStatus(newStatus);

    try {
      const toolName = selectedIntegration.name.toLowerCase().replace(/\s/g, '');
      const fileIds = selectedIntegrationFilesForUpload.map(f => f.id);
      await uploadDocumentFromIntegrationToProject(toolName, fileIds, documentType, projectId);
      
      // Mark all as success after successful bulk upload
      const successStatus = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
      selectedIntegrationFilesForUpload.forEach(file => {
        successStatus.set(file.name, 'success');
      });
      setUploadStatus(successStatus);

      await new Promise(resolve => setTimeout(resolve, 1000)); // Give a moment to see success
      setUploadDialogOpen(false);
      fetchFiles(page, rowsPerPage); // Refresh the document list
      fetchFolders();
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Documents from integration uploaded successfully.' }));

    } catch (error: any) {
      console.error('Upload from integration failed:', error);
      // Mark all as error on failure
      const errorStatus = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
      selectedIntegrationFilesForUpload.forEach(file => {
        errorStatus.set(file.name, 'error');
      });
      setUploadStatus(errorStatus);
      window.dispatchEvent(new CustomEvent('apiError', { detail: `Failed to upload documents from integration: ${error.message || 'Unknown error'}` }));
    } finally {
      setIsUploading(false);
      setUploadProgress(new Map()); // Clear progress
      setUploadStatus(new Map()); // Clear status
    }
  };


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // filteredFiles now directly uses project.files as filtering is done on backend
  const filteredFiles = project?.files || [];

  const handleChangeTeamPage = (event: unknown, newPage: number) => {
    setTeamPage(newPage);
  };

  const handleChangeTeamRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeamRowsPerPage(parseInt(event.target.value, 10));
    setTeamPage(0);
  };


  if (loading) return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ height: 40, bgcolor: 'action.hover', borderRadius: 1, width: '300px', mb: 1 }} />
          <Box sx={{ height: 20, bgcolor: 'action.hover', borderRadius: 1, width: '200px' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ height: 36, bgcolor: 'action.hover', borderRadius: 1, width: '120px' }} />
          <Box sx={{ height: 36, bgcolor: 'action.hover', borderRadius: 1, width: '80px' }} />
        </Box>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={{ height: 48, bgcolor: 'action.hover', borderRadius: 1, width: '80px' }} />
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <DocumentCardSkeleton key={`project-detail-skeleton-${index}`} />
        ))}
      </Box>
    </Box>
  );

  if (error || !project) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          {error || 'Project not found'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  const isOwner = project.ownerId === currentUser?.id;
  const canManageTeam = isOwner;
  const isDefaultProject = project.name.endsWith('Default Project');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleUploadTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setUploadTabValue(newValue);
    setSelectedFiles([]);
    setSelectedFolderName(null);
    setSelectedIntegrationFilesForUpload([]);
  };

  const handleAddMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      if (files.length > 5) {
        window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only upload a maximum of 5 files at a time.' }));
        return;
      }
      setSelectedFiles(files);
      setSelectedFolderName(null);
    }
  };

  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        const folderName = files[0].webkitRelativePath.split('/')[0];
        setSelectedFolderName(folderName);
        setSelectedFiles(files);
      }
    }
  };

  const handleDragEvents = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    handleDragEvents(e);
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 5) {
        window.dispatchEvent(new CustomEvent('apiWarning', { detail: 'You can only upload a maximum of 5 files at a time.' }));
        return;
      }
      setSelectedFiles(files);
      setSelectedFolderName(null);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setSelectedFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    setSelectedFolderName(null);
  };

  const handleUploadClick = async () => {
    if (!project || selectedFiles.length === 0) return;

    setIsUploading(true);
    
    const newProgress = new Map<string, number>();
    const newStatus = new Map<string, 'pending' | 'uploading' | 'success' | 'error'>();
    
    selectedFiles.forEach(file => {
      newProgress.set(file.name, 0);
      newStatus.set(file.name, 'pending');
    });
    
    setUploadProgress(newProgress);
    setUploadStatus(newStatus);
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        setUploadStatus(prev => new Map(prev).set(file.name, 'uploading'));
        
        try {
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => {
              const current = prev.get(file.name) || 0;
              if (current < 90) {
                return new Map(prev).set(file.name, current + 10);
              }
              return prev;
            });
          }, 200);
          
          await uploadFileToProject(project.id, [file], documentType, undefined);
          
          clearInterval(progressInterval);
          
          setUploadProgress(prev => new Map(prev).set(file.name, 100));
          setUploadStatus(prev => new Map(prev).set(file.name, 'success'));
          
        } catch (error: any) {
          console.error(`Upload failed for ${file.name}:`, error);
          
          const errorDetail = error.response?.data?.detail || error.response?.data?.message || '';
          if (errorDetail.startsWith('password_required:')) {
            setPasswordPrompt({ file, fileName: file.name });
            setIsUploading(false);
            return;
          }
          
          setUploadStatus(prev => new Map(prev).set(file.name, 'error'));
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setSelectedFolderName(null);
      fetchProjectStats(); // Refresh project stats
      await fetchFiles(page, rowsPerPage);
      await fetchFolders();
      
      setUploadProgress(new Map());
      setUploadStatus(new Map());
      
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddFileClick = () => {
    setUploadType('file');
    setUploadDialogOpen(true);
    handleAddMenuClose();
  };

  const handleSelectFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSelectFolderClick = () => {
    if (folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  const handleInvite = async () => {
    if (!userToInvite || !project) {
      return;
    }
    setIsInvitingUser(true);
    try {
      const response = await addUserToProject({
        project_id: project.id,
        user_id: userToInvite.id
      });
      const newUser: ProjectUser = {
        ...response,
        user: userToInvite
      };
      setUsers(prevUsers => [...prevUsers, newUser]);
      // Refetch project users to ensure the list is up-to-date
      const projectUsersResponse = await getProjectUsers(projectId!, { page: teamPage + 1, size: teamRowsPerPage });
      setUsers(projectUsersResponse.members);
      setTotalTeamMembers(projectUsersResponse.total);
      // Clear organization users so they are refetched next time the dialog opens
      setOrganizationUsers([]);
    } catch (error) {
      console.error('Failed to add user to project', error);
    } finally {
      setInviteDialogOpen(false);
      setUserToInvite(null);
      setIsInvitingUser(false);
    }
  };

  const handleRemoveClick = (user: ProjectUser) => {
    setUserToRemove(user);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (userToRemove && project) {
      setIsRemovingUser(true);
      try {
        await removeUserFromProject({
          project_id: project.id,
          user_id: userToRemove.user.id,
        });
        const updatedUsers = users.filter(
          (user) => user.id !== userToRemove.id
        );
        setUsers(updatedUsers);
        // Refetch project users to ensure the list is up-to-date
        const projectUsersResponse = await getProjectUsers(projectId!, { page: teamPage + 1, size: teamRowsPerPage });
        setUsers(projectUsersResponse.members);
        setTotalTeamMembers(projectUsersResponse.total);
        // Clear organization users so they are refetched next time the dialog opens
        setOrganizationUsers([]);
      } catch (error) {
        console.error('Failed to remove user', error);
      } finally {
        setRemoveDialogOpen(false);
        setUserToRemove(null);
        setIsRemovingUser(false);
      }
    }
  };

  const handleDownloadFile = async (file: ProjectFile) => {
    if (!project) return;
    setDownloadingFileIds(prev => new Set(prev).add(file.id));
    try {
      await downloadFile(file.id, file.name);
    } catch (error) {
      console.error('Failed to download file', error);
    } finally {
      setDownloadingFileIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
    }
  };

  const handleDownloadFolder = async (folder: ProjectFolder) => {
    if (!project) return;
    setDownloadingFolderIds(prev => new Set(prev).add(folder.id));
    try {
      await downloadFolder(project.id, folder.id, folder.name);
    } catch (error) {
      console.error('Failed to download folder', error);
    } finally {
      setDownloadingFolderIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(folder.id);
        return newSet;
      });
    }
  };

  const handleDeleteFile = (file: ProjectFile) => {
    setFileToDelete(file);
    setDeleteFileDialogOpen(true);
  };

  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete || !project) {
      return;
    }
    setIsDeletingFile(true);
    try {
      await deleteFile(fileToDelete.id);
      fetchProjectStats(); // Refresh project stats
      await fetchFiles(page, rowsPerPage);
      await fetchFolders();
      // await fetchProcessingStats(); // Refresh processing stats
    } catch (error) {
      console.error('Failed to delete file', error);
    } finally {
      setIsDeletingFile(false);
      setDeleteFileDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDeleteFolder = (folder: ProjectFolder) => {
    setFolderToDelete(folder);
    setDeleteFolderDialogOpen(true);
  };

  const handleConfirmDeleteFolder = async () => {
    if (!folderToDelete || !project) {
      return;
    }
    setIsDeletingFolder(true);
    try {
      await deleteFolder(project.id, folderToDelete.id);
      fetchProjectStats(); // Refresh project stats
      await fetchFolders();
      // await fetchProcessingStats(); // Refresh processing stats
    } catch (error) {
      console.error('Failed to delete folder', error);
    } finally {
      setIsDeletingFolder(false);
      setDeleteFolderDialogOpen(false);
      setFolderToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (!project || selected.length === 0) return;

    setIsBulkDeleting(true);
    try {
      await bulkDeleteFiles(selected);
      window.dispatchEvent(new CustomEvent('apiSuccess', { detail: 'Selected items deleted successfully.' }));
      fetchProjectStats(); // Refresh project stats
      await fetchFiles(page, rowsPerPage);
      await fetchFolders();
      // await fetchProcessingStats(); // Refresh processing stats
      setSelected([]);
    } catch (error) {
      console.error('Failed to bulk delete items', error);
      window.dispatchEvent(new CustomEvent('apiError', { detail: 'Failed to delete selected items.' }));
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };
  const handleDeleteProject = () => {
    setDeleteProjectDialogOpen(true);
  };

  const handleConfirmDeleteProject = async () => {
    if (!project) return;
    setIsDeletingProject(true);
    try {
      await deleteProject(project.id);
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project', error);
      setError('Failed to delete project.');
    } finally {
      setIsDeletingProject(false);
      setDeleteProjectDialogOpen(false);
      setDeleteConfirmationText('');
    }
  };

  const handleEditProject = () => {
    setProjectToEdit(project);
    setEditProjectDialogOpen(true);
  };

  const handleUpdateProject = async () => {
    if (!projectToEdit) return;
    setIsUpdatingProject(true);
    try {
      const updatedProject = await updateProject(projectToEdit.id, {
        name: projectToEdit.name,
        description: projectToEdit.description,
        stage: projectToEdit.stage,
        priority: projectToEdit.priority,
        is_private: projectToEdit.isPrivate,
        tags: projectToEdit.tags,
      });
      setProject(prevProject => {
        if (!prevProject) return null;
        const transformed = {
            ...updatedProject,
            organizationId: updatedProject.organization_id,
            ownerId: updatedProject.owner_id,
            createdDate: updatedProject.created_at,
            updatedDate: updatedProject.updated_at,
            tags: updatedProject.tags || [],
            users: updatedProject.team_members || [],
            queryCount: updatedProject.query_count || 0,
            totalSize: updatedProject.total_size || 0,
            fileCount: updatedProject.file_count || 0,
            lifecycle: updatedProject.lifecycle || ProjectLifecycle.InProcess,
            stage: updatedProject.stage || ProjectStage.Initiation,
            isPrivate: updatedProject.is_private,
        };
        return {
            ...prevProject,
            ...transformed,
        };
      });
      setEditProjectDialogOpen(false);
    } catch (error) {
      console.error('Failed to update project', error);
      setError('Failed to update project.');
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleSelect = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const handleSelectAll = () => {
    if (tabValue === 0) {
      const allFileIds = (project?.files || []).map((item) => item.id);
      setSelected(allFileIds);
    } else if (tabValue === 1) {
      const allFolderIds = (project?.folders || []).map((item) => item.id);
      setSelected(allFolderIds);
    }
  };

  const handleClearSelection = () => {
    setSelected([]);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Completed"
            size="small"
            color="success"
            sx={{ minWidth: '120px' }}
          />
        );
      case 'in_progress':
        return (
          <Chip
            icon={<AccessTimeIcon />}
            label="In Progress"
            size="small"
            color="primary"
            sx={{ minWidth: '120px' }}
          />
        );
      case 'needs_review':
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Needs Review"
            size="small"
            color="warning"
            sx={{ minWidth: '120px' }}
          />
        );
      default:
        return (
          <Chip
            icon={<HourglassEmptyIcon />}
            label="Not Started"
            size="small"
            color="default"
            sx={{ minWidth: '120px' }}
          />
        );
    }
  };

  const handleAssignDialogOpen = async (question: any) => {
    setCurrentQuestion(question);
    setAssignDialogOpen(true);
    if (project?.organizationId) {
      setReviewerCandidatesLoading(true);
      try {
        const orgUsersResponse = await getOrganizationUsers(project.organizationId);
        const activeOrgUsers = orgUsersResponse.members.filter((orgUser: any) => orgUser.user.is_active);
        setReviewerCandidates(activeOrgUsers);
      } catch (err) {
        console.error('Failed to fetch organization users for reviewer assignment', err);
      } finally {
        setReviewerCandidatesLoading(false);
      }
    }
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setCurrentQuestion(null);
  };

  const handleStatusDialogOpen = (question: any) => {
    setCurrentQuestion(question);
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  const FileItem: React.FC<{ file: ProjectFile; level?: number }> = ({ file, level = 0 }) => {
    const isItemSelected = isSelected(file.id);
    return (
      <TableRow
        hover
        onClick={() => handleSelect(file.id)}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={file.id}
        selected={isItemSelected}
      >
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={isItemSelected}
          />
        </TableCell>
        <TableCell component="th" scope="row" sx={{ pl: 2 + level * 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <FileIcon sx={{ mr: 1 }} />
              {file.name}
            </Box>
            {(file as any).tags && (file as any).tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(file as any).tags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag === 'ai-ready' ? 'AI Ready' : tag}
                    size="small"
                    color={tag === 'ai-ready' ? 'success' : 'default'}
                    variant={tag === 'ai-ready' ? 'filled' : 'outlined'}
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: '20px',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell>{file.file_size ? formatBytes(file.file_size) : '-'}</TableCell>
        <TableCell>{file.uploaded_at ? formatDate(file.uploaded_at) : '-'}</TableCell>
          <TableCell align="right">
            <IconButton 
              edge="end" 
              aria-label="download" 
              onClick={(e) => { e.stopPropagation(); handleDownloadFile(file); }} 
              sx={{ mr: 1 }}
              disabled={downloadingFileIds.has(file.id)}
            >
              {downloadingFileIds.has(file.id) ? (
                <CircularProgress size={20} />
              ) : (
                <DownloadIcon sx={{ fontSize: '1.2rem' }} />
              )}
            </IconButton>
            <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }}>
              <DeleteIcon sx={{ fontSize: '1.2rem' }} color="error" />
            </IconButton>
          </TableCell>
      </TableRow>
    );
  };

  const FolderItem: React.FC<{ folder: ProjectFolder; level?: number }> = ({ folder, level = 0 }) => {
    const isExpanded = expandedFolders.has(folder.id);
    const isItemSelected = isSelected(folder.id);

    return (
      <>
        <TableRow
          hover
          onClick={() => handleSelect(folder.id)}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          key={folder.id}
          selected={isItemSelected}
        >
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isItemSelected}
            />
          </TableCell>
          <TableCell component="th" scope="row" sx={{ pl: 2 + level * 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }}>
              {isExpanded ? <ExpandMoreIcon sx={{ mr: 1 }} /> : <ChevronRightIcon sx={{ mr: 1 }} />}
              <FolderIcon sx={{ mr: 1 }} />
              {folder.name}
            </Box>
          </TableCell>
          <TableCell>-</TableCell>
          <TableCell>{folder.createdDate ? formatDate(folder.createdDate) : '-'}</TableCell>
          <TableCell align="right">
            <IconButton 
              edge="end" 
              aria-label="download" 
              onClick={(e) => { e.stopPropagation(); handleDownloadFolder(folder); }} 
              sx={{ mr: 1 }}
              disabled={downloadingFolderIds.has(folder.id)}
            >
              {downloadingFolderIds.has(folder.id) ? (
                <CircularProgress size={20} />
              ) : (
                <DownloadIcon sx={{ fontSize: '1.2rem' }} />
              )}
            </IconButton>
            <IconButton edge="end" aria-label="delete" onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}>
              <DeleteIcon sx={{ fontSize: '1.2rem' }} color="error" />
            </IconButton>
          </TableCell>
        </TableRow>
        {isExpanded && folder.children && (
          <>
            {folder.children.map((child: ProjectFile | ProjectFolder) =>
              (child as ProjectFolder).is_folder ? (
                <FolderItem key={child.id} folder={child as ProjectFolder} level={level + 1} />
              ) : (
                <FileItem key={child.id} file={child as ProjectFile} level={level + 1} />
              )
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {project.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {projectStats.fileCount} files ({formatBytes(projectStats.totalSize)})
            {projectStats.aiReadyCount > 0 && (
              <> • {projectStats.aiReadyCount} AI Ready</>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMenuClick}
          >
            Add
          </Button>
          {isOwner && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditProject}
                sx={{ mr: 1 }}
                disabled={isDefaultProject}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteProject}
                disabled={isDefaultProject}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { color: 'text.secondary' },
          '& .Mui-selected': { color: 'text.primary' },
        }}
      >
        <Tab label="Files" />
        <Tab label="Query Table" />
        <Tab label="DDQ" />
        <Tab label="Team" />
        {project.deal && (
          <Tab label="Deal Details" />
        )}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Paper>
          {selected.length > 0 && (
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                bgcolor: 'action.selected',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="subtitle1"
                component="div"
              >
                {selected.length} selected
              </Typography>
              <Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ mr: 1 }}
                >
                  Select All
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearSelection}
                  sx={{ color: 'text.secondary' }}
                >
                  Clear Selection
                </Button>
                <Tooltip title="Delete">
                  <IconButton onClick={handleBulkDelete}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>
          )}
          <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filesLoading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRowSkeleton key={`files-skeleton-${index}`} columns={5} />
                  ))
                ) : filteredFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No documents found.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload new documents to get started.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleAddFileClick}
                      >
                        Upload Document
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFiles.map((file) => (
                    <FileItem key={file.id} file={file} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalFiles}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <QueryTable project={{
          id: project.id,
          name: project.name!,
          file_count: project.fileCount,
          total_size: project.totalSize
        }} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={ddqTabValue}
            onChange={(event, newValue) => setDdqTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="ddq sub tabs"
          >
            <Tab label="All Questions" />
            <Tab label="Needs Review" />
            <Tab label="Completed" />
            <Tab label="Unassigned" />
          </Tabs>
        </Paper>
          
        {ddqLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
        ) : ddqError ? (
            <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>{ddqError}</Typography>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Paper
                component="form"
                sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 400 }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search questions..."
                  value={ddqSearchQuery}
                  onChange={(e) => setDdqSearchQuery(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                />
              </Paper>
            </Box>
            
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reviewer</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ddqQuestions.filter(q => q.question_text && q.question_text.toLowerCase().includes(ddqSearchQuery.toLowerCase())).map((question) => (
                    <TableRow
                      key={question.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ai-drafts/${question.id}`)}
                    >
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Tooltip title={question.question_text}>
                          <Typography noWrap>{question.question_text}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {question.assignee ? (
                          <Tooltip title={question.assignee.email}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography>{question.assignee.name || question.assignee.email.split('@')[0]}</Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography color="text.secondary">Unassigned</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          onClick={(e) => { e.stopPropagation(); handleStatusDialogOpen(question); }}
                          sx={{ cursor: 'pointer', display: 'inline-block' }}
                        >
                          {getStatusChip(question.status)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {question.reviewer ? (
                          <Tooltip title={question.reviewer.email}>
                            <Typography>{question.reviewer.name || question.reviewer.email.split('@')[0]}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography color="text.secondary">Not assigned</Typography>
                        )}
                      </TableCell>
                      <TableCell>{question.updated_at ? formatDate(question.updated_at) : '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAssignDialogOpen(question); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Comment">
                            <IconButton size="small">
                              <CommentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {ddqQuestions.filter(q => q.question_text && q.question_text.toLowerCase().includes(ddqSearchQuery.toLowerCase())).length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No questions found
                </Typography>
              </Box>
            )}
          </Paper>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalDdqQuestions}
          rowsPerPage={ddqRowsPerPage}
          page={ddqPage}
          onPageChange={(event, newPage) => setDdqPage(newPage)}
          onRowsPerPageChange={(event) => {
            setDdqRowsPerPage(parseInt(event.target.value, 10));
            setDdqPage(0);
          }}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <Paper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2 }}>
            <Typography variant="h6">Team Members</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canManageTeam || isDefaultProject}
              onClick={() => setInviteDialogOpen(true)}
            >
              Add User
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamLoading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRowSkeleton key={`team-skeleton-${index}`} columns={4} />
                  ))
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No team members found.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add new team members to this project.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setInviteDialogOpen(true)}
                      >
                        Add Team Member
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32, fontSize: '0.875rem' }}>
                            {member.user.email.charAt(0).toUpperCase()}
                          </Avatar>
                          {member.user.email.split('@')[0]}
                        </Box>
                      </TableCell>
                      <TableCell>{member.user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.role}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canManageTeam && member.role?.toLowerCase() !== 'owner' && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveClick(member)}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalTeamMembers}
            rowsPerPage={teamRowsPerPage}
            page={teamPage}
            onPageChange={handleChangeTeamPage}
            onRowsPerPageChange={handleChangeTeamRowsPerPage}
          />
        </Paper>
      </TabPanel>

      {/* Deal Details Tab Panel - only shown for projects converted from deals */}
      {project.deal && (
        <TabPanel value={tabValue} index={4} sx={{ p: 0 }}>
          <DealDetailsCard dealData={project} />
        </TabPanel>
      )}

      <Menu
        anchorEl={addMenuAnchorEl}
        open={Boolean(addMenuAnchorEl)}
        onClose={handleAddMenuClose}
      >
        <MenuItem onClick={handleAddFileClick}>
          <FileIcon sx={{ mr: 1 }} fontSize="small" />
          Add File
        </MenuItem>
      </Menu>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <input
        type="file"
        ref={folderInputRef}
        onChange={handleFolderChange}
        style={{ display: 'none' }}
        webkitdirectory=""
      />

      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setSelectedFiles([]);
          setSelectedFolderName(null);
          setSelectedIntegrationFilesForUpload([]);
          setSelectedIntegration(null);
          setUploadTabValue(0); // Reset to "Upload New" tab
          setCurrentFolderId('root'); // Reset current folder to root
          setFolderPath([{ id: 'root', name: 'My Drive' }]); // Reset folder path
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Upload Document(s)</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={uploadTabValue} onChange={handleUploadTabChange}>
              <Tab label="Upload New" icon={<UploadFileIcon />} iconPosition="start" />
              <Tab label="Integrations" icon={<CloudUploadIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          {uploadTabValue === 0 && (
            <Box sx={{ pt: 2 }}>
              {selectedFiles.length === 0 ? (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    mt: 2,
                    bgcolor: isDragging ? 'action.hover' : 'transparent',
                  }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragEvents}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={uploadType === 'file' ? handleSelectFileClick : handleSelectFolderClick}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                    Drag & drop files here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to select {uploadType === 'file' ? 'files' : 'a folder'}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Document Type</InputLabel>
                    <Select
                      value={documentType}
                      label="Document Type"
                      onChange={(e) => setDocumentType(e.target.value as string)}
                    >
                      <MenuItem value="lease">Lease</MenuItem>
                      <MenuItem value="jv_agreement">JV Agreement</MenuItem>
                      <MenuItem value="psa">PSA</MenuItem>
                      <MenuItem value="loan_doc">Loan Doc</MenuItem>
                      <MenuItem value="offering_memorandum">Offering Memorandum</MenuItem>
                      <MenuItem value="sponsor_model">Sponsor Model</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedFolderName ? `Selected folder: ${selectedFolderName}` : `${selectedFiles.length} file(s) selected`}
                    </Typography>
                    {selectedFiles.length > 1 && (
                      <Button size="small" onClick={handleClearAllFiles}>Clear all</Button>
                    )}
                  </Box>
                  <Stack spacing={1.5}>
                    {selectedFiles.map((file, index) => {
                      const fileStatus = uploadStatus.get(file.name) || 'pending';
                      const fileProgress = uploadProgress.get(file.name) || 0;
                      
                      return (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: 'background.default',
                            borderColor: fileStatus === 'success' ? 'success.main' :
                                        fileStatus === 'error' ? 'error.main' :
                                        fileStatus === 'uploading' ? 'primary.main' : 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: fileStatus === 'uploading' ? 1 : 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', flex: 1 }}>
                                <Avatar sx={{
                                    bgcolor: fileStatus === 'success' ? 'success.light' :
                                            fileStatus === 'error' ? 'error.light' :
                                            fileStatus === 'uploading' ? 'primary.light' : 'primary.light',
                                    mr: 1.5
                                }}>
                                    {fileStatus === 'uploading' ? (
                                        <CircularProgress size={20} sx={{ color: 'white' }} />
                                    ) : (
                                        <FilePresentIcon sx={{ color: 'white' }} />
                                    )}
                                </Avatar>
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                        {selectedFolderName ? file.webkitRelativePath : file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatBytes(file.size)}
                                        {fileStatus === 'uploading' && ` • Uploading ${fileProgress}%`}
                                        {fileStatus === 'success' && ' • ✓ Uploaded'}
                                        {fileStatus === 'error' && ' • ✗ Failed'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isUploading && (
                                <IconButton size="small" onClick={() => handleRemoveFile(file)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                          </Box>
                          {fileStatus === 'uploading' && (
                              <LinearProgress
                                  variant="determinate"
                                  value={fileProgress}
                                  sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: 'action.hover'
                                  }}
                              />
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
          {uploadTabValue === 1 && (
            <Box sx={{ pt: 2 }}>
              {selectedIntegration ? (
                <>
                  {integrationFilesLoading ? (
                    <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {folderPath.map((folder, index) => (
                                  <React.Fragment key={folder.id}>
                                      {index < folderPath.length - 1 ? (
                                          <Button
                                              onClick={() => {
                                                  const newPath = folderPath.slice(0, index + 1);
                                                  setFolderPath(newPath);
                                                  setCurrentFolderId(folder.id);
                                                  if (selectedIntegration) {
                                                      fetchIntegrationFiles(selectedIntegration, folder.id);
                                                  }
                                              }}
                                          >
                                              {folder.name}
                                          </Button>
                                      ) : (
                                          <Typography variant="h6" sx={{ color: 'text.primary' }}>{folder.name}</Typography>
                                      )}
                                      {index < folderPath.length - 1 && <ChevronRightIcon />}
                                  </React.Fragment>
                              ))}
                          </Box>
                          <Box>
                              <Tooltip title="Refresh files">
                                  <IconButton onClick={() => fetchIntegrationFiles(selectedIntegration, currentFolderId)}>
                                      <RefreshIcon />
                                  </IconButton>
                              </Tooltip>
                              <Button variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => {
                                setSelectedIntegration(null);
                                setSelectedIntegrationFilesForUpload([]);
                                setCurrentFolderId('root');
                                setFolderPath([{ id: 'root', name: 'My Drive' }]);
                              }} sx={{ ml: 1 }}>Back to Integrations</Button>
                          </Box>
                      </Box>
                      <Paper sx={{ maxHeight: 250, overflow: 'auto' }}>
                        <TableContainer>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Modified</TableCell>
                                <TableCell>Size</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {isSubfolderLoading ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    <CircularProgress />
                                  </TableCell>
                                </TableRow>
                              ) : (
                                integrationFiles.map((file) => (
                                  <TableRow
                                    key={file.id}
                                    hover
                                    sx={{
                                      cursor: file.isFolder ? 'pointer' : 'default',
                                      '&:hover': file.isFolder ? {
                                        bgcolor: 'action.hover',
                                      } : {}
                                    }}
                                    onClick={() => {
                                      if (file.isFolder) {
                                        setCurrentFolderId(file.id);
                                        setFolderPath([...folderPath, { id: file.id, name: file.name }]);
                                        if (selectedIntegration) {
                                          fetchIntegrationFiles(selectedIntegration, file.id);
                                        }
                                      }
                                    }}
                                  >
                                    <TableCell padding="checkbox">
                                      {!file.isFolder && (
                                        <Checkbox
                                          checked={selectedIntegrationFilesForUpload.some(f => f.id === file.id)}
                                          onChange={(e) => {
                                            handleIntegrationFileSelect(file, e.target.checked);
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {file.isFolder ? (
                                          <>
                                            <FolderIcon fontSize="small" color="primary" />
                                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                              {file.name}
                                            </Typography>
                                            <ChevronRightIcon fontSize="small" color="action" />
                                          </>
                                        ) : (
                                          <>
                                            <FileIcon fontSize="small" color="action" />
                                            <Typography variant="body2" noWrap>
                                              {file.name}
                                            </Typography>
                                          </>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {file.modified_time ? new Date(file.modified_time).toLocaleDateString() : '-'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {file.size ? formatBytes(file.size) : '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </>
                  )}
                </>
              ) : (
                <>
                  {integrationsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : noIntegrationsConnected ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Integrations Connected
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        It looks like you haven't connected any cloud storage integrations yet.
                        Connect your accounts to seamlessly upload documents.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setUploadDialogOpen(false);
                          navigate('/integrations'); // Navigate to the integrations page
                        }}
                      >
                        Go to Integrations
                      </Button>
                    </Box>
                  ) : (
                    <List>
                      {integrations.map((integration) => (
                        <ListItem
                          key={integration.id}
                          secondaryAction={
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!integration.isConnected}
                              onClick={() => {
                                setSelectedIntegration(integration);
                                const integrationName = integration.name.toLowerCase();
                                let initialFolderId = 'root';
                                if (integrationName === 'dropbox') {
                                  initialFolderId = '';
                                } else if (integrationName === 'box') {
                                  initialFolderId = '0';
                                }
                                setCurrentFolderId(initialFolderId);
                                setFolderPath([{ id: initialFolderId, name: integration.name }]);
                              }}
                            >
                              {integration.isConnected ? 'Open' : 'Connect'}
                            </Button>
                          }
                        >
                          <ListItemIcon>
                            <img
                              src={`/logos/${integration.name.toLowerCase().replace(/\s/g, '')}.png`}
                              alt={`${integration.name} logo`}
                              width="24"
                              height="24"
                              style={{ objectFit: 'contain' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={integration.name}
                            secondary={
                              integration.isConnected ? (
                                <Typography variant="caption" color="green">
                                  Connected
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Disconnected
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Box>
          )}
          {selectedIntegrationFilesForUpload.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {`${selectedIntegrationFilesForUpload.length} file(s) selected from integrations`}
                </Typography>
                <Button size="small" onClick={() => setSelectedIntegrationFilesForUpload([])}>Clear all</Button>
              </Box>
              <Stack spacing={1.5}>
                {selectedIntegrationFilesForUpload.map((file, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.default'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                      <Avatar sx={{ bgcolor: 'secondary.light', mr: 1.5 }}>
                        <FileIcon sx={{ color: 'white' }} />
                      </Avatar>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatBytes(file.size)} ({file.source})
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={() => setSelectedIntegrationFilesForUpload(p => p.filter(f => f.id !== file.id))}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFiles([]);
              setSelectedFolderName(null);
              setSelectedIntegrationFilesForUpload([]);
              setSelectedIntegration(null);
              setUploadTabValue(0); // Reset to "Upload New" tab
              setCurrentFolderId('root'); // Reset current folder to root
              setFolderPath([{ id: 'root', name: 'My Drive' }]); // Reset folder path
            }}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={uploadTabValue === 0 ? handleUploadClick : handleIntegrationUpload}
            disabled={(selectedFiles.length === 0 && selectedIntegrationFilesForUpload.length === 0) || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
          >
            {isUploading ? 'Uploading...' : `Upload ${uploadTabValue === 0 ? selectedFiles.length : selectedIntegrationFilesForUpload.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={organizationUsers.filter(orgUser =>
              !users.some(projectUser => projectUser.user_id === orgUser.user.id)
            )}
            getOptionLabel={(option) => option.user.email}
            onChange={(event, newValue) => {
              setUserToInvite(newValue ? newValue.user : null);
            }}
            loading={organizationUsersLoading} // Show loader when fetching organization users
            loadingText="Loading users..."
            renderInput={(params) => (
              <TextField
                {...params}
                label="User"
                margin="dense"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {organizationUsersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInvite} disabled={isInvitingUser}>
            {isInvitingUser ? <CircularProgress size={24} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
      >
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {userToRemove?.user.email} from this project?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRemove} color="error" disabled={isRemovingUser}>
            {isRemovingUser ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteFileDialogOpen}
        onClose={() => setDeleteFileDialogOpen(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="error" sx={{ mr: 1 }} />
          Delete File
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the file "{fileToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteFileDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteFile}
            disabled={isDeletingFile}
            startIcon={isDeletingFile ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingFile ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteFolderDialogOpen}
        onClose={() => setDeleteFolderDialogOpen(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="error" sx={{ mr: 1 }} />
          Delete Folder
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the folder "{folderToDelete?.name}" and all its contents? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteFolderDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteFolder}
            disabled={isDeletingFolder}
            startIcon={isDeletingFolder ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingFolder ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="error" sx={{ mr: 1 }} />
          Delete Selected Items
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the {selected.length} selected items? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setBulkDeleteDialogOpen(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmBulkDelete}
            disabled={isBulkDeleting}
            startIcon={isBulkDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isBulkDeleting ? 'Deleting...' : `Delete ${selected.length} Items`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteProjectDialogOpen}
        onClose={() => {
          setDeleteProjectDialogOpen(false);
          setDeleteConfirmationText('');
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon color="error" sx={{ mr: 1 }} />
          Delete Project
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the project "{project?.name}"? This action cannot be undone.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Type 'delete' to confirm"
            type="text"
            fullWidth
            variant="outlined"
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => {
            setDeleteProjectDialogOpen(false);
            setDeleteConfirmationText('');
          }} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteProject}
            disabled={isDeletingProject || deleteConfirmationText.toLowerCase() !== 'delete'}
            startIcon={isDeletingProject ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingProject ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editProjectDialogOpen}
        onClose={() => setEditProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            label="Project Name"
            fullWidth
            margin="dense"
            value={projectToEdit?.name || ''}
            onChange={(e) =>
              setProjectToEdit(
                (prev) => prev && { ...prev, name: e.target.value }
              )
            }
          />
          <TextField
            label="Project Description"
            fullWidth
            margin="dense"
            multiline
            rows={4}
            value={projectToEdit?.description || ''}
            onChange={(e) =>
              setProjectToEdit(
                (prev) => prev && { ...prev, description: e.target.value }
              )
            }
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Stage</InputLabel>
            <Select
              value={projectToEdit?.stage || ''}
              label="Stage"
              onChange={(e) =>
                setProjectToEdit(
                  (prev) =>
                    prev && { ...prev, stage: e.target.value as ProjectStage }
                )
              }
            >
              {Object.entries(stageDescriptions).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {key}: {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select
              value={projectToEdit?.priority || ''}
              label="Priority"
              onChange={(e) =>
                setProjectToEdit(
                  (prev) =>
                    prev && {
                      ...prev,
                      priority: e.target.value as ProjectPriority,
                    }
                )
              }
            >
              {Object.values(ProjectPriority)
                .filter(priority => priority !== ProjectPriority.Critical)
                .map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={projectToEdit?.tags || []}
            onChange={(event, newValue) => {
              setProjectToEdit(
                (prev) => prev && { ...prev, tags: newValue }
              );
            }}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tags"
                placeholder="Add tags"
                margin="dense"
              />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={projectToEdit?.isPrivate || false}
                onChange={(e) =>
                  setProjectToEdit(
                    (prev) => prev && { ...prev, isPrivate: e.target.checked }
                  )
                }
              />
            }
            label="Private Project"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProjectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateProject}
            variant="contained"
            disabled={isUpdatingProject}
          >
            {isUpdatingProject ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={!!passwordPrompt} onClose={handlePasswordCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Password Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The PDF "{passwordPrompt?.fileName}" is password protected. Please enter the password to continue.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="PDF Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && password) {
                handlePasswordSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!password || isCheckingPassword}
            startIcon={isCheckingPassword ? <CircularProgress size={20} /> : null}
          >
            {isCheckingPassword ? 'Verifying...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose}>
        <DialogTitle>Assign Question</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            Question:
          </Typography>
          <Typography variant="body2" paragraph>
            {currentQuestion?.question_text}
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Assignee:
            </Typography>
            <Select
              value={currentQuestion?.assignee?.id || ''}
              onChange={(e) => {
                const selectedUserId = e.target.value;
                const selectedUser = reviewerCandidates.find(u => u.user.id === selectedUserId);
                setCurrentQuestion({
                  ...currentQuestion,
                  assignee: selectedUser ? selectedUser.user : null,
                });
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {reviewerCandidatesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                  <Typography sx={{ ml: 1 }}>Loading users...</Typography>
                </MenuItem>
              ) : (
                reviewerCandidates.map((member) => (
                  <MenuItem key={member.user.id} value={member.user.id}>
                    {member.user.name || member.user.email.split('@')[0]} ({member.role})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Reviewer:
            </Typography>
            <Select
              value={currentQuestion?.reviewer?.id || ''}
              onChange={(e) => {
                const selectedUserId = e.target.value;
                const selectedUser = reviewerCandidates.find(u => u.user.id === selectedUserId);
                setCurrentQuestion({
                  ...currentQuestion,
                  reviewer: selectedUser ? selectedUser.user : null,
                });
              }}
              displayEmpty
            >
              <MenuItem value="">
                <em>Not assigned</em>
              </MenuItem>
              {reviewerCandidatesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                  <Typography sx={{ ml: 1 }}>Loading users...</Typography>
                </MenuItem>
              ) : (
                reviewerCandidates.map((member) => (
                  <MenuItem key={member.user.id} value={member.user.id}>
                    {member.user.name || member.user.email.split('@')[0]} ({member.role})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button
            onClick={async () => {
              if (currentQuestion && projectId) {
                try {
                  await updateDDQQuestion(
                    currentQuestion.id,
                    currentQuestion.assignee?.id,
                    currentQuestion.reviewer?.id
                  );
                  // Refresh DDQ questions after updating
                  const data = await getDDQQuestions(projectId, 'all');
                  setDdqQuestions(data.questions || []);
                } catch (err) {
                  console.error('Failed to update DDQ question', err);
                }
              }
              handleAssignDialogClose();
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose}>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            Question:
          </Typography>
          <Typography variant="body2" paragraph>
            {currentQuestion?.question_text}
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Status:
            </Typography>
            <Typography variant="body1">
              {getStatusChip(currentQuestion?.status || 'not_started')}
            </Typography>
          </FormControl>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              New Status:
            </Typography>
            <Select
              value={currentQuestion?.status || 'not_started'}
            >
              <MenuItem value="not_started">Not Started</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="needs_review">Needs Review</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusDialogClose}>Cancel</Button>
          <Button onClick={handleStatusDialogClose} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
